import { useEffect, useRef } from "react";
import { AssistantErrorAlert } from "./AssistantErrorAlert";
import { IconX } from "@tabler/icons-react";
import { useShallow } from "zustand/react/shallow";
import { Button, Spinner } from "@onengine/component-library";
import { useFormStore } from "@/store/form-builder";
import { useAssistantStore } from "@/store/form-builder/useAssistantStore";
import { AssistantComposer } from "./AssistantComposer";
import { ChangeCard } from "./ChangeCard";
import { PlanCard } from "./PlanCard";
import styles from "./Assistant.module.scss";

interface AssistantPanelProps {
  tenantId?: string;
}

const BUSY_LABEL = {
  thinking: "Thinking",
  building: "Building the form in the preview",
} as const;

export function AssistantPanel({ tenantId }: AssistantPanelProps) {
  const {
    phase,
    messages,
    send,
    approvePlan,
    changePlan,
    cancelPlan,
    confirmChange,
    cancelChange,
    stop,
    reset,
    setOpen,
    buildOnOpen,
    requestOnOpen,
    runRequestOnOpen,
  } = useAssistantStore(
    useShallow((s) => ({
      phase: s.phase,
      messages: s.messages,
      send: s.send,
      approvePlan: s.approvePlan,
      changePlan: s.changePlan,
      cancelPlan: s.cancelPlan,
      confirmChange: s.confirmChange,
      cancelChange: s.cancelChange,
      stop: s.stop,
      reset: s.reset,
      setOpen: s.setOpen,
      buildOnOpen: s.buildOnOpen,
      requestOnOpen: s.requestOnOpen,
      runRequestOnOpen: s.runRequestOnOpen,
    })),
  );
  const listEndRef = useRef<HTMLDivElement>(null);
  const isBusy = phase === "thinking" || phase === "building";
  const context = () => {
    const { pages, getCompactJSON } = useFormStore.getState();
    return { tenantId, draftJSON: getCompactJSON(), draftPages: pages };
  };

  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages.length, phase]);

  // Asked for in the assignments assistant: build the approved plan, or propose
  // the confirmed change, right away. Nothing reaches the form until Apply.
  useEffect(() => {
    if (buildOnOpen) void approvePlan(context());
  }, [buildOnOpen]);

  useEffect(() => {
    if (requestOnOpen) void runRequestOnOpen(context());
  }, [requestOnOpen]);

  return (
    <aside className={styles.panel} aria-label="Form assistant">
      <header className={styles.panelHeader}>
        <div className={styles.panelTitle}>
          <span className={styles.cardTitle}>Assistant</span>
          <span className={styles.cardMeta}>Reads your forms. Proposes. You apply.</span>
        </div>
        <div className={styles.panelHeaderActions}>
          <Button variant="tertiary" size="small" onClick={reset}>
            New chat
          </Button>
          <Button
            type="icon"
            variant="tertiary"
            size="small"
            iconLeft={<IconX size={16} />}
            onClick={() => setOpen(false)}
            htmlAttributes={{ "aria-label": "Close assistant" }}
          />
        </div>
      </header>

      <div className={styles.messages} aria-live="polite">
        <p className={styles.assistantBubble}>
          Describe the form you need. I will show you a plan first, then build it here in the
          preview so you can try it before anything is saved.
        </p>
        {messages.map((entry) => {
          switch (entry.kind) {
            case "user":
              return (
                <p key={entry.id} className={styles.userBubble}>
                  {entry.text}
                </p>
              );
            case "assistant":
              return (
                <p key={entry.id} className={styles.assistantBubble}>
                  {entry.text}
                </p>
              );
            case "note":
              return (
                <p key={entry.id} className={styles.note}>
                  {entry.text}
                </p>
              );
            case "error":
              return <AssistantErrorAlert key={entry.id} text={entry.text} reference={entry.ref} />;
            case "plan":
              return (
                <PlanCard
                  key={entry.id}
                  plan={entry.plan}
                  status={entry.status}
                  isBusy={isBusy}
                  onApprove={() => void approvePlan(context())}
                  onChange={changePlan}
                  onCancel={cancelPlan}
                />
              );
            case "change":
              return (
                <ChangeCard
                  key={entry.id}
                  summary={entry.summary}
                  status={entry.status}
                  onConfirm={confirmChange}
                  onCancel={cancelChange}
                />
              );
          }
        })}
        {isBusy && (
          <p className={styles.busy}>
            <Spinner size="small" />
            {BUSY_LABEL[phase]}
          </p>
        )}
        <div ref={listEndRef} />
      </div>

      <AssistantComposer
        isBusy={isBusy}
        onSend={(text) => void send(text, context())}
        onStop={stop}
      />
    </aside>
  );
}
